import React from "react";
import { IDDateRangeOption, TableRow } from "../../InsightsDashboard";
import {
  formatIDNumber,
  formatIDPercent,
  renderComparisonValue,
} from "../../utils";

import { useHistory, useLocation } from "react-router";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { camelCaseToTitleCase } from "src/utils/methods";
import { Img } from "src/app/components/Img/Img";
import placeholder from "src/images/placeholder-image.svg";
import { RootState } from "src/redux/reducers";
import Flag from "src/app/components/Flag/Flag";
import { SOCIAL_ICONS } from "src/types";
import { useSelector } from "react-redux";

import "./InsightsDashboardTable.scss";
import classNames from "classnames";

interface Props {
  rows: TableRow[];
  comparisonRows?: TableRow[];
  selectedBreakdown: string | null;
  selectedDateRange: IDDateRangeOption | null;
  maxRows: number;
}

const InsightsDashboardTable = (props: Props) => {
  const {
    rows,
    comparisonRows,
    selectedBreakdown,
    selectedDateRange,
    maxRows,
  } = props;

  const { showTrends, printing } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const history = useHistory();
  const { pathname } = useLocation();

  const displayImage = rows.some((row) => row.meta.icon !== null);

  const onRowClick = (row: TableRow) => {
    history.push({
      pathname,
      search: `?type=${selectedBreakdown}&value=${row.key}`,
    });
  };

  const renderComparisonValueWrapper = (value?: number | null) => {
    if (!showTrends) return null;

    return renderComparisonValue(value, selectedDateRange?.label);
  };

  const renderIcon = (row: TableRow) => {
    if (!displayImage) return null;
    if (selectedBreakdown === "country") {
      // @ts-ignore
      return <Flag countryCode={row.meta.icon} />;
    }
    if (selectedBreakdown === "platform") {
      return <img src={SOCIAL_ICONS[row.key]} alt={row.key} />;
    }
    return (
      <Img
        src={row.meta.icon ?? undefined}
        fallbackImageSrc={placeholder}
        alt={row.meta.key}
      />
    );
  };

  const renderRow = (row: TableRow, index: number) => {
    const comparisonRow = comparisonRows?.[index];

    return (
      <tr
        key={`${selectedBreakdown}-${row.key}`}
        className="insights-dashboard__row"
        onClick={() => onRowClick(row)}
      >
        <td>
          <div className="insights-dashboard__row-info">
            {renderIcon(row)}
            <span>
              <span className="insights-dashboard__row-info-title">
                {row.meta.title}
              </span>
              <div>{row.meta.subtitle}</div>
            </span>
          </div>
        </td>

        <td>
          {formatIDNumber(row.posts_count)}
          {renderComparisonValueWrapper(comparisonRow?.posts_count)}
        </td>

        <td>
          {formatIDNumber(row.unique_influencers)}
          {renderComparisonValueWrapper(comparisonRow?.unique_influencers)}
        </td>

        <td>
          {formatIDPercent(row.avg_er)}
          {renderComparisonValueWrapper(comparisonRow?.avg_er)}
        </td>

        <td>
          {formatIDNumber(row.sum_likes)}
          {renderComparisonValueWrapper(comparisonRow?.sum_likes)}
        </td>

        <td>
          {formatIDNumber(row.avg_likes)}
          {renderComparisonValueWrapper(comparisonRow?.avg_likes)}
        </td>

        <td>
          {formatIDNumber(row.sum_comments)}
          {renderComparisonValueWrapper(comparisonRow?.sum_comments)}
        </td>

        <td>
          {formatIDNumber(row.avg_comments)}
          {renderComparisonValueWrapper(comparisonRow?.avg_comments)}
        </td>

        <td>
          {formatIDNumber(row.sum_engagements)}
          {renderComparisonValueWrapper(comparisonRow?.sum_engagements)}
        </td>

        <td>
          {formatIDNumber(row.avg_engagements)}
          {renderComparisonValueWrapper(comparisonRow?.avg_engagements)}
        </td>

        <td>
          {formatIDNumber(row?.sum_followers)}
          {renderComparisonValueWrapper(comparisonRow?.sum_followers)}
        </td>

        <td>
          {formatIDNumber(row?.avg_followers)}
          {renderComparisonValueWrapper(comparisonRow?.avg_followers)}
        </td>

        <td>
          <span className={row.avg_sentiment_positive ? "green" : undefined}>
            {formatIDPercent(row.avg_sentiment_positive)}
          </span>
        </td>

        <td>{formatIDPercent(row.avg_sentiment_neutral)}</td>

        <td>
          <span className={row.avg_sentiment_negative ? "red" : undefined}>
            {formatIDPercent(row.avg_sentiment_negative)}
          </span>
        </td>
      </tr>
    );
  };

  const getHeight = () => {
    if (rows.length === maxRows) {
      return maxRows * 101 + 72.5;
    }

    return Math.min(maxRows * 101 + 72.5, window.innerHeight - 150);
  };

  return (
    <div
      className={classNames("insights-dashboard__table-wrapper", {
        "insights-dashboard__table-wrapper--printing": printing,
      })}
      style={{ height: getHeight() }}
    >
      <table className="insights-dashboard__table">
        <thead>
          <tr>
            <th>{camelCaseToTitleCase(selectedBreakdown || "")}</th>
            <th>
              <IDHFormattedMessage
                id="ws_publications"
                defaultMessage="Publications"
              />
            </th>
            <th>
              <IDHFormattedMessage id="ws_creators" defaultMessage="Creators" />
            </th>

            <th>
              <IDHFormattedMessage id="ws_avg_er" defaultMessage="Avg Er" />
            </th>

            <th>
              <IDHFormattedMessage id="ws_likes" defaultMessage="Likes" />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_avg_likes"
                defaultMessage="Avg Likes"
              />
            </th>
            <th>
              <IDHFormattedMessage id="ws_comments" defaultMessage="Comments" />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_avg_comments"
                defaultMessage="Avg Comments"
              />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_engagements"
                defaultMessage="Engagements"
              />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_avg_engagements"
                defaultMessage="Avg Engagements"
              />
            </th>

            <th>
              <IDHFormattedMessage
                id="ws_followers"
                defaultMessage="Followers"
              />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_avg_followers"
                defaultMessage="Avg Followers"
              />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_positive_sentiment"
                defaultMessage="Positive sentiment"
              />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_neutral_sentiment"
                defaultMessage="Neutral sentiment"
              />
            </th>
            <th>
              <IDHFormattedMessage
                id="ws_negative_sentiment"
                defaultMessage="Negative sentiment"
              />
            </th>
          </tr>
        </thead>

        <tbody>{rows.map((row, index) => renderRow(row, index))}</tbody>
      </table>
    </div>
  );
};

export default InsightsDashboardTable;
